/* eslint-disable @typescript-eslint/no-explicit-any */
import { escapeRegExp, isEmpty, isNil, uniqBy } from "lodash";
import parseHtml from "html-react-parser";

export function twoTimePeriodsOverlap(
  start1: Date,
  end1: Date,
  start2: Date,
  end2: Date
) {
  return !(
    new Date(start2) > new Date(end1) || new Date(end2) < new Date(start1)
  );
}

export function getFileNameFromAssetUrl(url: string) {
  if (!url) return "";
  const slashParts = url.split("/");
  const dashParts = slashParts[slashParts.length - 1]?.split("-");
  dashParts.shift();
  return dashParts?.join("-") || slashParts[slashParts.length - 1] || "";
}

export function camelCaseToHumanReadable(camelCaseString: string) {
  // Split the camelCaseString by uppercase letters
  // and then capitalize the first letter of each word
  const words = camelCaseString
    .replace(/([A-Z])/g, " $1")
    .trim()
    .split(" ");
  for (let i = 0; i < words.length; i++) {
    words[i] = words[i].charAt(0).toUpperCase() + words[i].slice(1);
  }
  // Join the words back together with spaces
  return words.join(" ");
}

export function convertMillisecondsToDate(ms: number) {
  const days = Math.floor(ms / (1000 * 60 * 60 * 24));
  const hours = Math.floor(
    (ms - Number(days) * (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)
  );
  const minutes = Math.floor(
    (ms -
      Number(days) * (1000 * 60 * 60 * 24) -
      Number(hours) * (1000 * 60 * 60)) /
      (1000 * 60)
  );

  let displayValue = "";
  if (days > 0)
    displayValue =
      displayValue + "" + days + (Number(days) > 1 ? "days " : "day ");
  if (hours > 0)
    displayValue =
      displayValue + "" + hours + (Number(hours) > 1 ? "hours " : "hour ");
  if (minutes > 0)
    displayValue =
      displayValue + "" + minutes + (Number(minutes) > 1 ? "mins " : "min ");
  return displayValue;
}

export function formatFileSize(bytes: number): string {
  if (!bytes) return "Unknown Size";
  if (bytes === 0) return "0 B";
  if (bytes < 0) return "0 B";

  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB", "TB", "PB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  const size = bytes / Math.pow(k, i);

  // Format to 1-2 decimal places, but remove trailing zeros
  const formattedSize = parseFloat(size.toFixed(2));

  return `${formattedSize} ${sizes[i]}`;
}

export function exists<T>(value: T | undefined | null): value is T {
  return !isNil(value);
}

export function nameToHslColor(
  name: string,
  saturation: number,
  lightness: number
): string {
  let hash = 0;
  for (let i = 0; i < name?.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }

  const hue = Math.abs(hash) % 360;
  return "hsl(" + hue + ", " + saturation + "%, " + lightness + "%)";
}

export function nameInitials(name: string) {
  return name
    .toUpperCase()
    .split(" ")
    .map((wordInPartnerName) => wordInPartnerName.charAt(0))
    .join("");
}

export function fullNameInitials(given: string, surname: string) {
  const fname = (given || "").charAt(0);
  const lname = (surname || "").charAt(0);
  return fname + lname;
}

export type nil = undefined | null;

export function filterNilFromCollection<T>(collection?: (T | nil)[]): T[] {
  if (isNil(collection) || isEmpty(collection)) return [];
  return collection.filter((element) => !isNil(element)) as T[];
}

export const getDeepValueOfObject = (from: object, selector: string) =>
  selector
    .replace(/\[([^\[\]]*)\]/g, ".$1.")
    .split(".")
    .filter((t) => t !== "")
    .reduce((prev: any, cur: any) => prev && prev[cur], from);

export const slugify = (str: string) => {
  str = String(str).toString();
  str = str.replace(/^\s+|\s+$/g, ""); // trim
  str = str.toLowerCase();

  // remove accents, swap ñ for n, etc
  const swaps: { [key: string]: any } = {
    0: ["°", "₀", "۰", "０"],
    1: ["¹", "₁", "۱", "１"],
    2: ["²", "₂", "۲", "２"],
    3: ["³", "₃", "۳", "３"],
    4: ["⁴", "₄", "۴", "٤", "４"],
    5: ["⁵", "₅", "۵", "٥", "５"],
    6: ["⁶", "₆", "۶", "٦", "６"],
    7: ["⁷", "₇", "۷", "７"],
    8: ["⁸", "₈", "۸", "８"],
    9: ["⁹", "₉", "۹", "９"],
    a: [
      "à",
      "á",
      "ả",
      "ã",
      "ạ",
      "ă",
      "ắ",
      "ằ",
      "ẳ",
      "ẵ",
      "ặ",
      "â",
      "ấ",
      "ầ",
      "ẩ",
      "ẫ",
      "ậ",
      "ā",
      "ą",
      "å",
      "α",
      "ά",
      "ἀ",
      "ἁ",
      "ἂ",
      "ἃ",
      "ἄ",
      "ἅ",
      "ἆ",
      "ἇ",
      "ᾀ",
      "ᾁ",
      "ᾂ",
      "ᾃ",
      "ᾄ",
      "ᾅ",
      "ᾆ",
      "ᾇ",
      "ὰ",
      "ά",
      "ᾰ",
      "ᾱ",
      "ᾲ",
      "ᾳ",
      "ᾴ",
      "ᾶ",
      "ᾷ",
      "а",
      "أ",
      "အ",
      "ာ",
      "ါ",
      "ǻ",
      "ǎ",
      "ª",
      "ა",
      "अ",
      "ا",
      "ａ",
      "ä",
    ],
    b: ["б", "β", "ب", "ဗ", "ბ", "ｂ"],
    c: ["ç", "ć", "č", "ĉ", "ċ", "ｃ"],
    d: [
      "ď",
      "ð",
      "đ",
      "ƌ",
      "ȡ",
      "ɖ",
      "ɗ",
      "ᵭ",
      "ᶁ",
      "ᶑ",
      "д",
      "δ",
      "د",
      "ض",
      "ဍ",
      "ဒ",
      "დ",
      "ｄ",
    ],
    e: [
      "é",
      "è",
      "ẻ",
      "ẽ",
      "ẹ",
      "ê",
      "ế",
      "ề",
      "ể",
      "ễ",
      "ệ",
      "ë",
      "ē",
      "ę",
      "ě",
      "ĕ",
      "ė",
      "ε",
      "έ",
      "ἐ",
      "ἑ",
      "ἒ",
      "ἓ",
      "ἔ",
      "ἕ",
      "ὲ",
      "έ",
      "е",
      "ё",
      "э",
      "є",
      "ə",
      "ဧ",
      "ေ",
      "ဲ",
      "ე",
      "ए",
      "إ",
      "ئ",
      "ｅ",
    ],
    f: ["ф", "φ", "ف", "ƒ", "ფ", "ｆ"],
    g: ["ĝ", "ğ", "ġ", "ģ", "г", "ґ", "γ", "ဂ", "გ", "گ", "ｇ"],
    h: ["ĥ", "ħ", "η", "ή", "ح", "ه", "ဟ", "ှ", "ჰ", "ｈ"],
    i: [
      "í",
      "ì",
      "ỉ",
      "ĩ",
      "ị",
      "î",
      "ï",
      "ī",
      "ĭ",
      "į",
      "ı",
      "ι",
      "ί",
      "ϊ",
      "ΐ",
      "ἰ",
      "ἱ",
      "ἲ",
      "ἳ",
      "ἴ",
      "ἵ",
      "ἶ",
      "ἷ",
      "ὶ",
      "ί",
      "ῐ",
      "ῑ",
      "ῒ",
      "ΐ",
      "ῖ",
      "ῗ",
      "і",
      "ї",
      "и",
      "ဣ",
      "ိ",
      "ီ",
      "ည်",
      "ǐ",
      "ი",
      "इ",
      "ی",
      "ｉ",
    ],
    j: ["ĵ", "ј", "Ј", "ჯ", "ج", "ｊ"],
    k: ["ķ", "ĸ", "к", "κ", "Ķ", "ق", "ك", "က", "კ", "ქ", "ک", "ｋ"],
    l: ["ł", "ľ", "ĺ", "ļ", "ŀ", "л", "λ", "ل", "လ", "ლ", "ｌ"],
    m: ["м", "μ", "م", "မ", "მ", "ｍ"],
    n: ["ñ", "ń", "ň", "ņ", "ŉ", "ŋ", "ν", "н", "ن", "န", "ნ", "ｎ"],
    o: [
      "ó",
      "ò",
      "ỏ",
      "õ",
      "ọ",
      "ô",
      "ố",
      "ồ",
      "ổ",
      "ỗ",
      "ộ",
      "ơ",
      "ớ",
      "ờ",
      "ở",
      "ỡ",
      "ợ",
      "ø",
      "ō",
      "ő",
      "ŏ",
      "ο",
      "ὀ",
      "ὁ",
      "ὂ",
      "ὃ",
      "ὄ",
      "ὅ",
      "ὸ",
      "ό",
      "о",
      "و",
      "θ",
      "ို",
      "ǒ",
      "ǿ",
      "º",
      "ო",
      "ओ",
      "ｏ",
      "ö",
    ],
    p: ["п", "π", "ပ", "პ", "پ", "ｐ"],
    q: ["ყ", "ｑ"],
    r: ["ŕ", "ř", "ŗ", "р", "ρ", "ر", "რ", "ｒ"],
    s: ["ś", "š", "ş", "с", "σ", "ș", "ς", "س", "ص", "စ", "ſ", "ს", "ｓ"],
    t: ["ť", "ţ", "т", "τ", "ț", "ت", "ط", "ဋ", "တ", "ŧ", "თ", "ტ", "ｔ"],
    u: [
      "ú",
      "ù",
      "ủ",
      "ũ",
      "ụ",
      "ư",
      "ứ",
      "ừ",
      "ử",
      "ữ",
      "ự",
      "û",
      "ū",
      "ů",
      "ű",
      "ŭ",
      "ų",
      "µ",
      "у",
      "ဉ",
      "ု",
      "ူ",
      "ǔ",
      "ǖ",
      "ǘ",
      "ǚ",
      "ǜ",
      "უ",
      "उ",
      "ｕ",
      "ў",
      "ü",
    ],
    v: ["в", "ვ", "ϐ", "ｖ"],
    w: ["ŵ", "ω", "ώ", "ဝ", "ွ", "ｗ"],
    x: ["χ", "ξ", "ｘ"],
    y: [
      "ý",
      "ỳ",
      "ỷ",
      "ỹ",
      "ỵ",
      "ÿ",
      "ŷ",
      "й",
      "ы",
      "υ",
      "ϋ",
      "ύ",
      "ΰ",
      "ي",
      "ယ",
      "ｙ",
    ],
    z: ["ź", "ž", "ż", "з", "ζ", "ز", "ဇ", "ზ", "ｚ"],
    aa: ["ع", "आ", "آ"],
    ae: ["æ", "ǽ"],
    ai: ["ऐ"],
    ch: ["ч", "ჩ", "ჭ", "چ"],
    dj: ["ђ", "đ"],
    dz: ["џ", "ძ"],
    ei: ["ऍ"],
    gh: ["غ", "ღ"],
    ii: ["ई"],
    ij: ["ĳ"],
    kh: ["х", "خ", "ხ"],
    lj: ["љ"],
    nj: ["њ"],
    oe: ["ö", "œ", "ؤ"],
    oi: ["ऑ"],
    oii: ["ऒ"],
    ps: ["ψ"],
    sh: ["ш", "შ", "ش"],
    shch: ["щ"],
    ss: ["ß"],
    sx: ["ŝ"],
    th: ["þ", "ϑ", "ث", "ذ", "ظ"],
    ts: ["ц", "ც", "წ"],
    ue: ["ü"],
    uu: ["ऊ"],
    ya: ["я"],
    yu: ["ю"],
    zh: ["ж", "ჟ", "ژ"],
    "(c)": ["©"],
    A: [
      "Á",
      "À",
      "Ả",
      "Ã",
      "Ạ",
      "Ă",
      "Ắ",
      "Ằ",
      "Ẳ",
      "Ẵ",
      "Ặ",
      "Â",
      "Ấ",
      "Ầ",
      "Ẩ",
      "Ẫ",
      "Ậ",
      "Å",
      "Ā",
      "Ą",
      "Α",
      "Ά",
      "Ἀ",
      "Ἁ",
      "Ἂ",
      "Ἃ",
      "Ἄ",
      "Ἅ",
      "Ἆ",
      "Ἇ",
      "ᾈ",
      "ᾉ",
      "ᾊ",
      "ᾋ",
      "ᾌ",
      "ᾍ",
      "ᾎ",
      "ᾏ",
      "Ᾰ",
      "Ᾱ",
      "Ὰ",
      "Ά",
      "ᾼ",
      "А",
      "Ǻ",
      "Ǎ",
      "Ａ",
      "Ä",
    ],
    B: ["Б", "Β", "ब", "Ｂ"],
    C: ["Ç", "Ć", "Č", "Ĉ", "Ċ", "Ｃ"],
    D: ["Ď", "Ð", "Đ", "Ɖ", "Ɗ", "Ƌ", "ᴅ", "ᴆ", "Д", "Δ", "Ｄ"],
    E: [
      "É",
      "È",
      "Ẻ",
      "Ẽ",
      "Ẹ",
      "Ê",
      "Ế",
      "Ề",
      "Ể",
      "Ễ",
      "Ệ",
      "Ë",
      "Ē",
      "Ę",
      "Ě",
      "Ĕ",
      "Ė",
      "Ε",
      "Έ",
      "Ἐ",
      "Ἑ",
      "Ἒ",
      "Ἓ",
      "Ἔ",
      "Ἕ",
      "Έ",
      "Ὲ",
      "Е",
      "Ё",
      "Э",
      "Є",
      "Ə",
      "Ｅ",
    ],
    F: ["Ф", "Φ", "Ｆ"],
    G: ["Ğ", "Ġ", "Ģ", "Г", "Ґ", "Γ", "Ｇ"],
    H: ["Η", "Ή", "Ħ", "Ｈ"],
    I: [
      "Í",
      "Ì",
      "Ỉ",
      "Ĩ",
      "Ị",
      "Î",
      "Ï",
      "Ī",
      "Ĭ",
      "Į",
      "İ",
      "Ι",
      "Ί",
      "Ϊ",
      "Ἰ",
      "Ἱ",
      "Ἳ",
      "Ἴ",
      "Ἵ",
      "Ἶ",
      "Ἷ",
      "Ῐ",
      "Ῑ",
      "Ὶ",
      "Ί",
      "И",
      "І",
      "Ї",
      "Ǐ",
      "ϒ",
      "Ｉ",
    ],
    J: ["Ｊ"],
    K: ["К", "Κ", "Ｋ"],
    L: ["Ĺ", "Ł", "Л", "Λ", "Ļ", "Ľ", "Ŀ", "ल", "Ｌ"],
    M: ["М", "Μ", "Ｍ"],
    N: ["Ń", "Ñ", "Ň", "Ņ", "Ŋ", "Н", "Ν", "Ｎ"],
    O: [
      "Ó",
      "Ò",
      "Ỏ",
      "Õ",
      "Ọ",
      "Ô",
      "Ố",
      "Ồ",
      "Ổ",
      "Ỗ",
      "Ộ",
      "Ơ",
      "Ớ",
      "Ờ",
      "Ở",
      "Ỡ",
      "Ợ",
      "Ø",
      "Ō",
      "Ő",
      "Ŏ",
      "Ο",
      "Ό",
      "Ὀ",
      "Ὁ",
      "Ὂ",
      "Ὃ",
      "Ὄ",
      "Ὅ",
      "Ὸ",
      "Ό",
      "О",
      "Θ",
      "Ө",
      "Ǒ",
      "Ǿ",
      "Ｏ",
      "Ö",
    ],
    P: ["П", "Π", "Ｐ"],
    Q: ["Ｑ"],
    R: ["Ř", "Ŕ", "Р", "Ρ", "Ŗ", "Ｒ"],
    S: ["Ş", "Ŝ", "Ș", "Š", "Ś", "С", "Σ", "Ｓ"],
    T: ["Ť", "Ţ", "Ŧ", "Ț", "Т", "Τ", "Ｔ"],
    U: [
      "Ú",
      "Ù",
      "Ủ",
      "Ũ",
      "Ụ",
      "Ư",
      "Ứ",
      "Ừ",
      "Ử",
      "Ữ",
      "Ự",
      "Û",
      "Ū",
      "Ů",
      "Ű",
      "Ŭ",
      "Ų",
      "У",
      "Ǔ",
      "Ǖ",
      "Ǘ",
      "Ǚ",
      "Ǜ",
      "Ｕ",
      "Ў",
      "Ü",
    ],
    V: ["В", "Ｖ"],
    W: ["Ω", "Ώ", "Ŵ", "Ｗ"],
    X: ["Χ", "Ξ", "Ｘ"],
    Y: [
      "Ý",
      "Ỳ",
      "Ỷ",
      "Ỹ",
      "Ỵ",
      "Ÿ",
      "Ῠ",
      "Ῡ",
      "Ὺ",
      "Ύ",
      "Ы",
      "Й",
      "Υ",
      "Ϋ",
      "Ŷ",
      "Ｙ",
    ],
    Z: ["Ź", "Ž", "Ż", "З", "Ζ", "Ｚ"],
    AE: ["Æ", "Ǽ"],
    Ch: ["Ч"],
    Dj: ["Ђ"],
    Dz: ["Џ"],
    Gx: ["Ĝ"],
    Hx: ["Ĥ"],
    Ij: ["Ĳ"],
    Jx: ["Ĵ"],
    Kh: ["Х"],
    Lj: ["Љ"],
    Nj: ["Њ"],
    Oe: ["Œ"],
    Ps: ["Ψ"],
    Sh: ["Ш"],
    Shch: ["Щ"],
    Ss: ["ẞ"],
    Th: ["Þ"],
    Ts: ["Ц"],
    Ya: ["Я"],
    Yu: ["Ю"],
    Zh: ["Ж"],
  };

  Object.keys(swaps).forEach((swap: string) => {
    swaps[swap].forEach((s: any) => {
      str = str.replace(new RegExp(s, "g"), swap);
    });
  });
  return str
    .replace(/[^a-z0-9 -]/g, "") // remove invalid chars
    .replace(/\s+/g, "-") // collapse whitespace and replace by -
    .replace(/-+/g, "-") // collapse dashes
    .replace(/^-+/, "") // trim - from start of text
    .replace(/-+$/, "");
};

const generateKeywordArray = (keywords: string[]) => {
  let keywordArray: string[] = [];
  keywords?.map((combinedKeyword: string) => {
    const tempKeywordArray = combinedKeyword
      .trim()
      .split(" ")
      .filter((v) => v);
    keywordArray = keywordArray.concat(tempKeywordArray);
  });
  keywordArray = uniqBy(keywordArray, (val) => val).filter(
    (item) => item.length
  );
  return keywordArray;
};
export function highLightWords(
  text: string,
  keywords: string[],
  highlightColor = "#ff8f0457"
) {
  if (!text) return "";
  if (!keywords?.length || !keywords[0]?.length) return text;
  const keywordArray = [...generateKeywordArray(keywords)];
  if (!keywordArray?.length) return text;
  let tempText = text;
  const highlightBlockBegin = `<b style='background-color:${highlightColor}; margin: 0px 1px;'>`;
  const highlightBlockEnd = `</b>`;
  const highlightBlockBeginAlias = "^^^^^";
  const highlightBlockEndAlias = "^^^";

  keywordArray.map((originalWord) => {
    const word = escapeRegExp(originalWord);
    const strRegExp = new RegExp("(?<!<[^>]*)" + word + "(?![^<]*>)", "ig");
    const matchArr = tempText.match(strRegExp);
    if (!matchArr?.length) return;
    matchArr.map((matchword, i) => {
      if (i > 0) return;
      let t = 0;
      tempText = tempText.replace(strRegExp, (match) => {
        return ++t === i + 1
          ? `${highlightBlockBeginAlias}${matchword}${highlightBlockEndAlias}`
          : match;
      });
    });
  });
  const finalResult = tempText
    .replaceAll(highlightBlockBeginAlias, highlightBlockBegin)
    .replaceAll(highlightBlockEndAlias, highlightBlockEnd);
  return parseHtml(finalResult);
}

export function stringToColor(string: string) {
  let hash = 0;
  let i;

  /* eslint-disable no-bitwise */
  for (i = 0; i < string.length; i += 1) {
    hash = string.charCodeAt(i) + ((hash << 5) - hash);
  }

  let color = "#";

  for (i = 0; i < 3; i += 1) {
    const value = (hash >> (i * 8)) & 0xff;
    color += `00${value.toString(16)}`.slice(-2);
  }
  /* eslint-enable no-bitwise */

  return color;
}

export function avatarString(name: string) {
  return `${name.split(" ")[0]?.[0] || ""}${name.split(" ")[1]?.[0] || ""}`;
}

export function isDiagnosisPassed(diagnosisResult: Record<string, boolean | undefined> | undefined) {
  return !diagnosisResult? null:
  diagnosisResult && Object.keys(diagnosisResult).length && Object.values(diagnosisResult).every(value => value === true) ? (
      true
    ) :  (
      false
    )
}
